import React, { useState, useRef, useEffect } from 'react';
import './OTPModal.css';

export default function OTPModal({
    show,
    onClose,
    onVerify,
    phoneNumber,
    onResend,
    isLoading = false,
    otpError,
    resendOtpLoading
}) {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [countdown, setCountdown] = useState(0);
    const inputRefs = useRef([]);

    useEffect(() => {
        if (show) {
            // Focus first input when modal opens
            setTimeout(() => {
                if (inputRefs.current[0]) {
                    inputRefs.current[0].focus();
                }
            }, 100);
        } else {
            // Reset OTP when modal closes
            setOtp(['', '', '', '', '', '']);
        }
    }, [show]);

    // Countdown timer effect
    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [countdown]);

    const handleInputChange = (index, value) => {
        // Only allow numbers
        if (!/^\d*$/.test(value)) return;

        // Handle single character input
        if (value.length <= 1) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Auto-focus next input
            if (value && index < 5) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace') {
            if (!otp[index] && index > 0) {
                // Move to previous input if current is empty
                inputRefs.current[index - 1]?.focus();
            } else {
                // Clear current input
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
            }
        }
        // Handle arrow keys
        else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const newOtp = [...otp];

        for (let i = 0; i < pastedData.length && i < 6; i++) {
            newOtp[i] = pastedData[i];
        }

        setOtp(newOtp);

        // Focus the next empty input or the last input
        const nextIndex = Math.min(pastedData.length, 5);
        inputRefs.current[nextIndex]?.focus();
    };

    const handleVerify = () => {
        const otpString = otp.join('');
        if (otpString.length === 6) {
            onVerify(otpString);
        }
    };

    const handleResend = () => {
        if (countdown === 0 && !resendOtpLoading) {
            onResend();
            setCountdown(60); // Start 60-second countdown
        }
    };

    const handleClose = () => {
        if (!isLoading && !resendOtpLoading) {
            onClose();
        }
    };

    const isOtpComplete = otp.every(digit => digit !== '');

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    if (!show) return null;

    return (
        <>
            <div className={`otp-modal-overlay ${show ? 'active' : ''}`} onClick={handleClose} />
            <div className={`otp-modal-wrapper ${show ? 'active' : ''}`}>
                <div className="otp-modal-container">
                    {/* Close Button */}
                    <button
                        className="otp-modal-close"
                        onClick={handleClose}
                        disabled={isLoading || resendOtpLoading}
                        aria-label="Close modal"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>

                    {/* Modal Header */}
                    <div className="otp-modal-header">
                        <div className="otp-icon-wrapper">
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="48" height="48" rx="24" fill="currentColor" fillOpacity="0.1"/>
                                <path d="M24 14V24L30 27M38 24C38 31.732 31.732 38 24 38C16.268 38 10 31.732 10 24C10 16.268 16.268 10 24 10C31.732 10 38 16.268 38 24Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <h2 className="otp-modal-title">Verify Your Email</h2>
                        <p className="otp-modal-subtitle">
                            We've sent a 6-digit verification code to
                            <br />
                            <strong>{phoneNumber}</strong>
                        </p>
                    </div>

                    {/* OTP Input Section */}
                    <div className="otp-modal-body">
                        <div className="otp-input-container">
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={el => inputRefs.current[index] = el}
                                    type="text"
                                    inputMode="numeric"
                                    className={`otp-input ${digit ? 'filled' : ''} ${otpError ? 'error' : ''}`}
                                    maxLength="1"
                                    value={digit}
                                    onChange={(e) => handleInputChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onPaste={handlePaste}
                                    disabled={isLoading || resendOtpLoading}
                                    aria-label={`OTP digit ${index + 1}`}
                                />
                            ))}
                        </div>

                        {/* Error Message */}
                        {otpError && (
                            <div className="otp-error-message">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1ZM8 10.5C7.72386 10.5 7.5 10.2761 7.5 10V8C7.5 7.72386 7.72386 7.5 8 7.5C8.27614 7.5 8.5 7.72386 8.5 8V10C8.5 10.2761 8.27614 10.5 8 10.5ZM8 6.5C7.72386 6.5 7.5 6.27614 7.5 6C7.5 5.72386 7.72386 5.5 8 5.5C8.27614 5.5 8.5 5.72386 8.5 6C8.5 6.27614 8.27614 6.5 8 6.5Z" fill="currentColor"/>
                                </svg>
                                <span>{otpError}</span>
                            </div>
                        )}

                        {/* Verify Button */}
                        <button
                            className="otp-verify-button"
                            onClick={handleVerify}
                            disabled={!isOtpComplete || isLoading || resendOtpLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner"></span>
                                    Verifying...
                                </>
                            ) : (
                                <>
                                    Verify OTP
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4.16666 10H15.8333M15.8333 10L10 4.16666M15.8333 10L10 15.8333" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </>
                            )}
                        </button>

                        {/* Resend Section */}
                        <div className="otp-resend-section">
                            <p className="otp-resend-text">Didn't receive the code?</p>
                            <button
                                className="otp-resend-button"
                                onClick={handleResend}
                                disabled={countdown > 0 || resendOtpLoading}
                            >
                                {resendOtpLoading ? (
                                    <>
                                        <span className="spinner small"></span>
                                        Sending...
                                    </>
                                ) : countdown > 0 ? (
                                    `Resend code in ${formatTime(countdown)}`
                                ) : (
                                    'Resend Code'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
